"use client";

import React, { useEffect, useRef } from "react";
import Link from "next/link";

const outcomes = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 6v6l4 2"/>
      </svg>
    ),
    title: "Reduce Manual Work",
    description: "Automate repetitive tasks across your operations — from data entry to report generation.",
    accent: "#A78BFA",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: "Streamline Onboarding",
    description: "Automate new employee setup, device provisioning, and access management across all sites.",
    accent: "#60A5FA",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
    title: "Automate Repetitive Processes",
    description: "Intelligent workflows that handle routine IT tasks, alerts, and escalations automatically.",
    accent: "#22D3EE",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: "Improve Business Efficiency",
    description: "Surface actionable insights from your IT environment to drive smarter business decisions.",
    accent: "#A78BFA",
  },
];

export default function AIAutomationSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll(".ai-reveal");
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden-reveal");
            entry.target.classList.add("animate-reveal");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items?.forEach((el) => { el?.classList?.add("hidden-reveal", "reveal"); observer?.observe(el); });
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-24 lg:py-32 relative overflow-hidden ai-gradient"
    >
      {/* Fluid background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="blob-1 absolute top-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
        />
        <div
          className="blob-2 absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
        />
        <div
          className="blob-3 absolute top-[40%] right-[20%] w-[400px] h-[400px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
      </div>
      <div className="absolute inset-0 pointer-events-none grid-overlay" />
      {/* Neural pathway SVG background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: 0.15 }}>
        <svg viewBox="0 0 1200 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="neuralGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#7C3AED" stopOpacity="0"/>
              <stop offset="50%" stopColor="#3B82F6" stopOpacity="1"/>
              <stop offset="100%" stopColor="#22D3EE" stopOpacity="0"/>
            </linearGradient>
          </defs>
          {/* Neural paths */}
          {[
            "M 0 300 Q 300 100 600 300 Q 900 500 1200 300",
            "M 0 200 Q 400 400 800 200 Q 1000 100 1200 250",
            "M 0 400 Q 200 200 500 350 Q 800 500 1200 350",
            "M 100 0 Q 300 300 600 150 Q 900 0 1100 300",
          ]?.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke="url(#neuralGrad)"
              strokeWidth="1"
              strokeDasharray="8 6"
              className="neural-path"
              style={{ animationDelay: `${i * 1}s` }}
            />
          ))}
          {/* Neural nodes */}
          {[
            { cx: 200, cy: 180 }, { cx: 450, cy: 320 }, { cx: 700, cy: 150 },
            { cx: 900, cy: 380 }, { cx: 1050, cy: 220 }, { cx: 350, cy: 450 },
          ]?.map((node, i) => (
            <circle
              key={i}
              cx={node?.cx} cy={node?.cy} r="4"
              fill="#7C3AED"
              opacity="0.6"
              className="network-pulse"
              style={{ animationDelay: `${i * 0.5}s` }}
            />
          ))}
        </svg>
      </div>
      <div className="relative z-10 max-w-container mx-auto px-4 sm:px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center w-full">

          {/* Left: Copy */}
          <div>
            <div className="ai-reveal stagger-1 section-sep mb-5" style={{ background: "linear-gradient(90deg, #7C3AED, #3B82F6)" }} />
            <div
              className="ai-reveal stagger-1 inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-6"
              style={{ border: "1px solid rgba(124,58,237,0.35)", background: "rgba(124,58,237,0.1)" }}
            >
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#A78BFA" }} />
              <span className="text-[11px] font-heading font-600 tracking-widest uppercase gradient-text">
                AI & Automation Solutions
              </span>
            </div>

            <h2
              className="ai-reveal stagger-2 font-heading font-700 leading-tight mb-5"
              style={{ fontSize: "clamp(28px, 3.5vw, 48px)", color: "#F8FAFC" }}
            >
              Intelligent automation
              <br />
              <span className="gradient-text-violet">for modern operations.</span>
            </h2>

            <p
              className="ai-reveal stagger-3 text-[16px] font-body leading-relaxed mb-10"
              style={{ color: "rgba(203,213,225,0.75)" }}
            >
              We bring practical AI and automation to your business — not buzzwords, but real workflows
              that save time, reduce errors, and help your team focus on what matters.
            </p>

            <div className="ai-reveal stagger-4 flex flex-wrap gap-3 sm:gap-4">
              <Link
                href="/ai-automation"
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 text-[13px] sm:text-[14px] font-heading font-600 rounded-pill transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, #7C3AED, #4F46E5)",
                  color: "#F8FAFC",
                  boxShadow: "0 0 24px rgba(124,58,237,0.35)",
                }}
              >
                Explore AI & Automation Services
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link
                href="/book"
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 text-[13px] sm:text-[14px] font-heading font-500 btn-secondary"
              >
                Book a Discovery Call
              </Link>
            </div>
          </div>

          {/* Right: Outcome cards */}
          <div className="ai-reveal stagger-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {outcomes?.map((outcome, i) => (
              <div
                key={outcome?.title}
                className="glass-card rounded-card p-6 transition-all duration-300"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${outcome?.accent}15`, color: outcome?.accent }}
                >
                  {outcome?.icon}
                </div>
                <h3
                  className="font-heading font-600 text-[15px] mb-2"
                  style={{ color: "#F8FAFC" }}
                >
                  {outcome?.title}
                </h3>
                <p className="text-[14px] font-body leading-relaxed leading-[1.75]" style={{ color: "#94A3B8" }}>
                  {outcome?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
